<script lang="ts">
	import { Badge } from '$lib/components/ui/badge';
	import * as Empty from '$lib/components/ui/empty';
	import CardNew from '@/components/Cards/CardNew.svelte';
	import ProfileReviewCard from '@/components/profile/ProfileReviewCard.svelte';
	import { UsersExperienceOptions } from '@/pocketbase/pocketbase-types';
	import { pb } from '@/utils/ApiClient';
	import MailCheck from '@lucide/svelte/icons/mail-check';
	import ShieldCheck from '@lucide/svelte/icons/shield-check';
	import Star from '@lucide/svelte/icons/star';
	import type { PageData } from './$types';

	let { data }: { data: PageData } = $props();

	const userProfile = $derived(data.userProfile);
	const listings = $derived(data.listings);
	const reviews = $derived(data.reviews);
	const isArmador = $derived(data.isArmador);
	const hasBoat = $derived(data.hasBoat);
	const ratingAvg = $derived(data.ratingAvg);
	const ratingCount = $derived(data.ratingCount);

	const displayName = $derived(userProfile.name || userProfile.username);
	const avatarUrl = $derived(
		userProfile.avatar
			? pb.files.getURL(userProfile, userProfile.avatar, { thumb: '256x256' })
			: null
	);
	const memberSince = $derived(
		new Date(userProfile.created).toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'long'
		})
	);

	function formatRating(value: number | null): string {
		if (value === null) return '—';
		return value.toFixed(1);
	}

	function getExperienceLabel(exp: UsersExperienceOptions | undefined): string {
		switch (exp) {
			case UsersExperienceOptions.advanced:
				return 'Experto';
			case UsersExperienceOptions.intermediate:
				return 'Intermedio';
			default:
				return 'Novato';
		}
	}
</script>

<svelte:head>
	<title>{displayName} | Zarpar</title>
	<meta name="description" content={userProfile.bio || `Perfil de ${displayName} en Zarpar`} />
</svelte:head>

<div class="bg-white py-6 sm:py-10">
	<div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
		<div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
			<div class="flex w-full shrink-0 flex-col items-start sm:w-auto">
				{#if avatarUrl}
					<img
						src={avatarUrl}
						alt=""
						class="size-20 rounded-2xl object-cover outline-1 -outline-offset-1 outline-black/5 sm:size-24"
						width="96"
						height="96"
					/>
				{:else}
					<div
						class="flex size-20 items-center justify-center rounded-2xl bg-gray-100 text-2xl font-semibold text-gray-600 sm:size-24"
					>
						{displayName.charAt(0).toUpperCase()}
					</div>
				{/if}

				<p class="mt-2 text-base/6 text-gray-400 sm:text-sm/6">Miembro desde {memberSince}</p>

				<div class="mt-4 flex flex-col items-start gap-2">
					{#if userProfile.verified}
						<Badge variant="secondary" class="gap-1">
							<MailCheck class="size-3.5" />
							Email verificado
						</Badge>
					{/if}

					{#if isArmador}
						<Badge variant="secondary" class="gap-1">
							<ShieldCheck class="size-3.5" />
							Identidad verificada
						</Badge>
						<p class="text-base/6 text-gray-400 sm:text-sm/6">
							<span class="tabular-nums text-gray-600">{listings.length}</span>
							{listings.length === 1 ? ' anuncio activo' : ' anuncios activos'}
						</p>
					{:else if !hasBoat}
						<p class="text-base/6 text-gray-400 sm:text-sm/6">
							Experiencia náutica:
							<span class="text-gray-600">
								{getExperienceLabel(userProfile.experience)}
							</span>
						</p>
					{/if}

					{#if ratingCount > 0 && ratingAvg !== null}
						<a
							href="#profile-reviews"
							class="inline-flex items-center gap-1.5 text-base/6 text-gray-400 transition-colors hover:text-gray-700 sm:text-sm/6"
						>
							<Star class="size-4 fill-amber-400/80 text-amber-400/80" />
							<span class="tabular-nums text-gray-600">{formatRating(ratingAvg)}</span>
							<span>· {ratingCount} {ratingCount === 1 ? 'reseña' : 'reseñas'}</span>
						</a>
					{/if}
				</div>
			</div>

			<div class="min-w-0 flex-1">
				<h1 class="text-2xl/8 font-semibold text-gray-900 sm:text-xl/8">{displayName}</h1>
				<p class="mt-0.5 text-base/6 text-gray-500 sm:text-sm/6">@{userProfile.username}</p>

				<div class="mt-4">
					{#if userProfile.bio}
						<p class="text-base/7 text-gray-600 sm:text-sm/6">{userProfile.bio}</p>
					{:else}
						<p class="text-base/7 text-gray-400 italic sm:text-sm/6">
							Este usuario aún no ha escrito su biografía.
						</p>
					{/if}
				</div>
			</div>
		</div>

		<div class="mt-8 space-y-10">
			{#if isArmador}
				<section aria-labelledby="profile-listings-heading">
					<h2
						id="profile-listings-heading"
						class="text-base/6 font-medium text-gray-500 sm:text-sm/6"
					>
						Anuncios activos
					</h2>
					<div class="mt-3 grid grid-cols-2 gap-x-2 gap-y-4 sm:gap-4 lg:grid-cols-3">
						{#each listings as listing (listing.id)}
							<CardNew
								listing={listing}
								minPriceFromRanges={listing.minPriceFromRanges}
								aspectRatio="portrait"
								width={300}
								height={400}
							/>
						{/each}
					</div>
				</section>
			{/if}

			<section
				id="profile-reviews"
				class="scroll-mt-[calc(var(--site-nav-height,0px)+1rem)]"
				aria-labelledby="profile-reviews-heading"
			>
				<h2 id="profile-reviews-heading" class="text-base/6 font-medium text-gray-500 sm:text-sm/6">
					Reseñas
				</h2>

				<div class="mt-3">
					{#if reviews.length === 0}
						<Empty.Root
							class="rounded-2xl border border-dashed border-gray-950/10 bg-gray-50/60 py-10"
						>
							<Empty.Header>
								<Empty.Media variant="icon">
									<Star class="size-5 text-gray-400" />
								</Empty.Media>
								<Empty.Title class="text-base/7 sm:text-sm/6">Sin reseñas todavía</Empty.Title>
								<Empty.Description class="text-base/7 sm:text-sm/6">
									Cuando alguien valore a {displayName}, aparecerá aquí.
								</Empty.Description>
							</Empty.Header>
						</Empty.Root>
					{:else}
						<div class="flex flex-col gap-6">
							{#each reviews as review (review.id)}
								<ProfileReviewCard review={review} />
							{/each}
						</div>
					{/if}
				</div>
			</section>
		</div>
	</div>
</div>
